import React, { useState } from "react";
import { activityTypeToIconMap } from "@/components/layouts/Activities/components/Activity";
import { Clock, ExpandView, People } from "@dreamhub/designsystem/icons";
import Chip from "@/components/ui/chip";
import IconContainer from "@/components/ui/icon-container";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import Modal from "@/components/controls/Modal";
import ActivityDetails from "@/components/layouts/Activities/ActivityDetails/ActivityDetails";
import { Activity, ActivityData } from "@/entities/activities/types";
import {
  isMeetingActivity,
  isTranscriptProcessing
} from "@/entities/activities/helpers";
import TranscriptProcessingIndicator from "@/components/layouts/Activities/components/TranscriptProcessingIndicator";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import {
  useActivityPeople,
  useActivityTime,
  useActivityTitle,
  useActivityType
} from "@/services/hooks/useActivity";
import { simpleDate, toCalendarDate } from "@/utils/datetime";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { isEmpty, isNull } from "lodash";

interface Props {
  activity: ActivityData;
  onEdit?: (activity: ActivityData) => void;
  onDelete?: (activityId: string) => void;
}

const ActivityItem: React.FC<Props> = ({ activity, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExpandedView, setIsExpandedView] = useState(false);

  const title = useActivityTitle(activity);
  const type = useActivityType(t, activity);
  const people = useActivityPeople(activity?.peopleIds, activity?.notes);
  const detailsPeople = useActivityPeople(
    activity?.peopleIds,
    activity?.notes,
    true
  );
  const time = useActivityTime(activity?.notes);
  const icon = activityTypeToIconMap[activity?.type as Activity];

  const { enabled: processingIndicatorEnabled } = useFeatureFlag(
    FeatureFlags.CRM_8124_TRANSCRIPT_PROCESSING
  );
  const showProcessingIndicator =
    processingIndicatorEnabled &&
    isMeetingActivity(activity) &&
    isTranscriptProcessing(activity?.notes);

  const getDropdownOptions = (): DetailedViewOptions => {
    const options: DetailedViewOptions = {};
    if (onEdit) {
      options.edit = () => onEdit(activity);
    }
    if (onDelete) {
      options.delete = {
        action: () => onDelete(activity.id),
        variant: "destructive"
      };
    }
    return options;
  };

  const dateLabel = activity?.notes?.date
    ? `${simpleDate(toCalendarDate(activity.notes.date))}${time ? ` ${time}` : ""}`
    : null;

  return (
    <div
      className={cn(
        "group flex flex-col gap-1 p-2 rounded-xl transition-colors duration-200 ease-in-out",
        isDropdownOpen ? "bg-white-4" : "hover:bg-white-4"
      )}
    >
      <div className="flex gap-2 items-center w-full">
        <div className="flex flex-1 min-w-0 gap-2 items-center">
          <LogoIconContainer
            index={-1}
            bgColor="var(--grey-800)"
            className="!size-6 !rounded-md p-px"
            innerClassName="!rounded-[5px]"
            iconSize="sm"
            icon={icon}
          />
          <Text variant="label" className="truncate" hasTruncatedTooltip>
            {title}
          </Text>
          <Text variant="bodySecondary" className="shrink-0">
            {`(${type})`}
          </Text>
        </div>
        <div
          className={cn(
            "flex gap-1 items-center shrink-0 opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100",
            isDropdownOpen && "opacity-100"
          )}
        >
          <IconButton
            variant="icon"
            size="md"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpandedView(true);
            }}
          >
            <ExpandView />
          </IconButton>
          {(onEdit || onDelete) && (
            <OptionsDropdownMenu
              options={getDropdownOptions()}
              tPrefix="activities.options"
              buttonVariant="icon"
              iconSize="md"
              onOpenChange={setIsDropdownOpen}
            />
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center pl-6 w-full">
        {!isEmpty(activity?.peopleIds) && (
          <Chip
            size="lg"
            preview
            icon={
              <IconContainer className="!size-[18px]" color="secondary">
                <People />
              </IconContainer>
            }
            label={people}
            textVariant="bodySecondary"
            textClassName="text-text-body-secondary"
          />
        )}
        {!isNull(dateLabel) && (
          <Chip
            size="lg"
            preview
            icon={
              <IconContainer className="!size-[18px]" color="secondary">
                <Clock />
              </IconContainer>
            }
            label={dateLabel}
            textVariant="bodySecondary"
            textClassName="text-text-body-secondary"
          />
        )}
      </div>
      {showProcessingIndicator && (
        <TranscriptProcessingIndicator className="ml-6" />
      )}
      <Modal
        open={isExpandedView}
        setIsOpen={(open) => {
          if (!open) setIsExpandedView(false);
        }}
        showCloseButton={false}
        hideDivider
        headerClassname="hidden"
        contentClassname="w-[64rem] h-fit"
        content={
          <ActivityDetails
            activity={activity}
            title={title}
            icon={icon}
            activityType={type}
            closePopover={() => setIsExpandedView(false)}
            people={detailsPeople}
            time={time}
            openEdit={onEdit ? (_activityType, activityData) => onEdit(activityData) : undefined}
            openDelete={onDelete ? (activityId) => onDelete(activityId) : undefined}
            isExpandedView={isExpandedView}
          />
        }
      />
    </div>
  );
};

export default ActivityItem;
